import { useCallback, useEffect, useState } from 'react'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, ListPlus, Loader2, Pause, Pin as PinIcon, Play, Trash2, X, Zap } from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent } from '@orisini/ui/components/card'
import { api, ApiError } from '@orisini/ui/lib/api'
import { cn } from '@orisini/ui/lib/utils'
import { usePagination } from '@/components/pagination'
import { accountsApi, type AccountDto } from '@/features/accounts/api'
import { pinsApi, type PinDto } from '@/features/pins/api'

type QueueAction = 'hold' | 'unhold' | 'cancel' | 'unpin' | 'post-now'

function fmtSchedule(ms: number | null, tz: string): string {
  if (!ms) return 'waiting for its turn'
  return new Date(ms).toLocaleString('en-GB', {
    timeZone: tz,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function QueueRow({
  pin,
  tz,
  onAction,
}: {
  pin: PinDto
  tz: string
  onAction: (action: QueueAction, pin: PinDto) => void
}) {
  const sortable = useSortable({ id: pin.id, disabled: pin.pinMode === 'fixed' })
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  }

  return (
    <div
      ref={sortable.setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 rounded-lg border bg-card p-3',
        sortable.isDragging && 'z-10 opacity-80 shadow-lg',
      )}
    >
      {pin.pinMode === 'auto' ? (
        <button
          {...sortable.attributes}
          {...sortable.listeners}
          className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
          title="Drag to reorder"
        >
          <GripVertical className="size-4" />
        </button>
      ) : (
        <span title="Fixed schedule — not part of the queue order">
          <PinIcon className="size-4 text-primary" />
        </span>
      )}

      <div className="size-12 shrink-0 overflow-hidden rounded bg-muted">
        {pin.media?.previewUrl && pin.media.kind === 'image' ? (
          <img src={pin.media.previewUrl} alt="" className="size-full object-cover" loading="lazy" />
        ) : (
          <div className="flex size-full items-center justify-center text-[10px] text-muted-foreground">
            {pin.media?.kind ?? '—'}
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <Link to={`/pins/${pin.id}`} className="block truncate text-sm font-medium hover:underline">
          {pin.title || pin.description || '(untitled)'}
        </Link>
        <p className="text-xs text-muted-foreground">
          {pin.onHold ? (
            <span className="text-amber-500">On hold</span>
          ) : (
            <>goes live: {fmtSchedule(pin.scheduledAt, tz)}</>
          )}
          {pin.board && <> · {pin.board.name}</>}
          {pin.pinMode === 'fixed' && <> · fixed schedule</>}
        </p>
      </div>

      <div className="flex shrink-0 gap-1">
        {pin.pinMode === 'fixed' && (
          <Button variant="ghost" size="sm" onClick={() => onAction('unpin', pin)}>
            To queue
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground hover:text-primary"
          title="Post now"
          onClick={() => onAction('post-now', pin)}
        >
          <Zap className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          title={pin.onHold ? 'Resume' : 'Hold'}
          onClick={() => onAction(pin.onHold ? 'unhold' : 'hold', pin)}
        >
          {pin.onHold ? <Play className="size-4" /> : <Pause className="size-4" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground hover:text-destructive"
          title="Cancel"
          onClick={() => onAction('cancel', pin)}
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  )
}

/**
 * Pin yang belum masuk antrean — draft dan yang pernah dibatalkan.
 *
 * Ia ikut di tab Antrean karena tidak punya tempat lain: tab Terbit hanya berisi
 * pin yang sudah tayang, sehingga tanpa bagian ini draft yang disimpan dari
 * editor tidak akan pernah terlihat lagi.
 */
function DraftRow({
  pin,
  busy,
  onEnqueue,
  onDelete,
}: {
  pin: PinDto
  busy: boolean
  onEnqueue: (pin: PinDto) => void
  onDelete: (pin: PinDto) => void
}) {
  const siap = pin.problems.length === 0

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-card p-3">
      <div className="size-12 shrink-0 overflow-hidden rounded bg-muted">
        {pin.media?.previewUrl && pin.media.kind === 'image' ? (
          <img src={pin.media.previewUrl} alt="" className="size-full object-cover" loading="lazy" />
        ) : (
          <div className="flex size-full items-center justify-center text-[10px] text-muted-foreground">
            {pin.media?.kind ?? 'empty'}
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <Link to={`/pins/${pin.id}`} className="block truncate text-sm font-medium hover:underline">
          {pin.title || pin.description || '(untitled)'}
        </Link>
        <p className="truncate text-xs text-muted-foreground">
          {pin.status === 'cancelled' && <span className="text-muted-foreground">Cancelled · </span>}
          {pin.board ? pin.board.name : <span className="text-amber-500">no board yet</span>}
          {!siap && <span className="text-amber-500"> · not ready: {pin.problems.join(', ')}</span>}
        </p>
      </div>

      <Button
        variant="outline"
        size="sm"
        className="shrink-0"
        disabled={busy || !siap}
        title={siap ? 'Add to queue' : `Complete first: ${pin.problems.join(', ')}`}
        onClick={() => onEnqueue(pin)}
      >
        {busy ? <Loader2 className="size-4 animate-spin" /> : <ListPlus className="size-4" />}
        Queue
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
        title="Delete"
        onClick={() => onDelete(pin)}
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  )
}

export default function QueuePanel() {
  const [accounts, setAccounts] = useState<AccountDto[]>([])
  const [accountId, setAccountId] = useState('')
  const [queue, setQueue] = useState<PinDto[]>([])
  const [drafts, setDrafts] = useState<PinDto[]>([])
  const [tz, setTz] = useState('Asia/Jakarta')
  const [loading, setLoading] = useState(true)
  /** id pin yang aksinya sedang berjalan — tombolnya dikunci sementara. */
  const [busyId, setBusyId] = useState<string | null>(null)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const { visible, pager, resetPage } = usePagination(queue, 'pins')

  useEffect(() => {
    accountsApi
      .list()
      .then((res) => {
        setAccounts(res.accounts)
        if (res.accounts.length) {
          setAccountId((prev) => prev || res.accounts[0].id)
          setTz(res.accounts[0].timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  // Ganti akun berarti antrean lain sama sekali — mulai lagi dari halaman 1.
  useEffect(() => {
    resetPage()
  }, [accountId, resetPage])

  const load = useCallback(async (accId: string) => {
    const [antrean, belum] = await Promise.all([
      api.get<{ queue: PinDto[] }>(`/accounts/${accId}/queue`),
      pinsApi.list({ accountId: accId, status: 'draft,cancelled', limit: 200 }),
    ])
    setQueue(antrean.queue)
    setDrafts(belum.pins)
  }, [])

  useEffect(() => {
    if (accountId) {
      void load(accountId).catch((e: unknown) =>
        toast.error(e instanceof ApiError ? e.message : 'Couldn\'t load queue'),
      )
      const acc = accounts.find((a) => a.id === accountId)
      if (acc) setTz(acc.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone)
    }
  }, [accountId, accounts, load])

  async function onDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    // Urutan visual hanya berlaku antar pin AUTO; pin fixed tidak digeser.
    const ids = queue.map((p) => p.id)
    const from = ids.indexOf(String(active.id))
    const to = ids.indexOf(String(over.id))
    const optimistic = arrayMove(queue, from, to)
    setQueue(optimistic)

    // Tetangga baru dihitung dari daftar hasil pindah, hanya pin auto yang dihitung.
    const autoIds = optimistic.filter((p) => p.pinMode === 'auto').map((p) => p.id)
    const idx = autoIds.indexOf(String(active.id))
    const beforeId = idx > 0 ? autoIds[idx - 1] : null
    const afterId = idx < autoIds.length - 1 ? autoIds[idx + 1] : null

    try {
      await api.post(`/accounts/${accountId}/queue/reorder`, {
        pinId: active.id,
        beforeId,
        afterId,
      })
      await load(accountId)
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t reorder')
      await load(accountId)
    }
  }

  async function onAction(action: QueueAction, pin: PinDto) {
    try {
      if (action === 'cancel') {
        toast('Cancel this pin from the queue?', {
          action: {
            label: 'Cancel',
            onClick: () => {
              api
                .post(`/pins/${pin.id}/cancel`)
                .then(() => load(accountId))
                .catch((e: unknown) =>
                  toast.error(e instanceof ApiError ? e.message : 'Couldn\'t cancel'),
                )
            },
          },
        })
        return
      }

      // Posting nyata & tidak bisa ditarik kembali — minta konfirmasi dulu.
      if (action === 'post-now') {
        toast('Post this pin now?', {
          description:
            'The pin skips the queue and is scheduled immediately. The account\'s minimum gap between posts still applies.',
          action: {
            label: 'Post',
            onClick: () => {
              api
                .post(`/pins/${pin.id}/post-now`)
                .then(() => {
                  toast.success('Pin scheduled now — the agent will process it')
                  return load(accountId)
                })
                .catch((e: unknown) =>
                  toast.error(e instanceof ApiError ? e.message : 'Couldn\'t post now'),
                )
            },
          },
        })
        return
      }
      await api.post(`/pins/${pin.id}/${action}`)
      await load(accountId)
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Action failed')
    }
  }

  async function onEnqueueDraft(pin: PinDto) {
    setBusyId(pin.id)
    try {
      await pinsApi.enqueue(pin.id)
      toast.success('Pin added to the queue')
      await load(accountId)
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t add to the queue')
    } finally {
      setBusyId(null)
    }
  }

  function onDeleteDraft(pin: PinDto) {
    toast('Delete this pin?', {
      action: {
        label: 'Delete',
        onClick: () => {
          pinsApi
            .remove(pin.id)
            .then(() => {
              toast.success('Pin deleted')
              return load(accountId)
            })
            .catch((err: unknown) =>
              toast.error(err instanceof ApiError ? err.message : 'Couldn\'t delete'),
            )
        },
      },
    })
  }

  const scheduled = queue.filter((p) => p.scheduledAt)
  const lastAt = scheduled.at(-1)?.scheduledAt

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {queue.length} pins waiting
          {lastAt && (
            <>
              {' '}
              · queue filled until{' '}
              {new Date(lastAt).toLocaleDateString('en-GB', {
                timeZone: tz,
                day: 'numeric',
                month: 'short',
              })}
            </>
          )}
        </p>
        {accounts.length > 1 && (
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {queue.length === 0 ? (
        <Card>
          <CardContent className="space-y-3 py-10 text-center text-sm text-muted-foreground">
            <p>Queue is empty.</p>
            <p>
              Queue a draft from the list below, or upload many images at once via{' '}
              <Link to="/pins/bulk" className="text-foreground underline">
                Bulk input
              </Link>
              .
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            {/* Hanya baris yang terlihat yang bisa ditarik; tetangga tetap dihitung
                dari antrean utuh di onDragEnd, jadi urutan lintas halaman aman. */}
            <SortableContext items={visible.map((p) => p.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {visible.map((pin) => (
                  <QueueRow key={pin.id} pin={pin} tz={tz} onAction={onAction} />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {pager}
        </>
      )}

      {drafts.length > 0 && (
        <section className="space-y-2 border-t pt-6">
          <h2 className="text-sm font-medium">Not queued yet ({drafts.length})</h2>
          <p className="text-xs text-muted-foreground">
            These pins are saved but won't go live until added to the queue.
          </p>
          <div className="space-y-2 pt-1">
            {drafts.map((pin) => (
              <DraftRow
                key={pin.id}
                pin={pin}
                busy={busyId === pin.id}
                onEnqueue={(p) => void onEnqueueDraft(p)}
                onDelete={onDeleteDraft}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
